import React, { createContext, useContext, useMemo, useSyncExternalStore } from "react";
import { createAuthStore, type AuthRootState, type AuthAppDispatch } from "./store";

type AuthStore = ReturnType<typeof createAuthStore>;

const AuthStoreContext = createContext<AuthStore | null>(null);

function useAuthStore(): AuthStore {
  const store = useContext(AuthStoreContext);
  if (!store) {
    throw new Error("Auth hooks must be used within AuthStoreProvider");
  }
  return store;
}

export function useAuthSelector<T>(selector: (state: AuthRootState) => T): T {
  const store = useAuthStore();
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getState()),
  );
}

export const useAuthDispatch = (): AuthAppDispatch => {
  return useAuthStore().dispatch;
};

interface AuthStoreProviderProps {
  children: React.ReactNode;
}

export function AuthStoreProvider({ children }: AuthStoreProviderProps) {
  const store = useMemo(() => createAuthStore(), []);

  return (
    <AuthStoreContext.Provider value={store}>
      {children}
    </AuthStoreContext.Provider>
  );
}

export type { AuthRootState, AuthAppDispatch };
